import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  selectCurrentChat,
  setGroupList,
  setOpenedGroup,
} from "@/context/features/chat/chatSlice";
import { useGetListChatsGroupQuery } from "@/graphql/generated";
import { UserRound } from "lucide-react";
import { useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import ChatList from "./chat-list";
import { useTranslation } from "react-i18next";
import { useListenToGroupChange } from "@/hooks/livekit/useListenToGroupCreate";
import { useLivekitContext } from "@/context/features/livekit/LivekitRoomContext";
import { selectCurrentRoom } from "@/context/features/room/roomSlice";
import { selectCurrentAuth } from "@/context/features/auth/authSlice";
import { Role } from "@/types/role.enum";
import UnreadBadge from "./unread-badge";

export default function ChatControll({
  activeTab,
}: {
  activeTab: "patients_list" | "chats" | "files";
}) {
  const { openedGroup } = useSelector(selectCurrentChat);
  const { user } = useSelector(selectCurrentAuth);
  const { patientLine, unreadMessges } = useSelector(
    selectCurrentRoom,
    shallowEqual
  );
  const { open, openMobile } = useSidebar();

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const shouldQuery = (open || openMobile) && activeTab === "chats";
  const { livekitContext } = useLivekitContext();

  const { data, loading, error, refetch } = useGetListChatsGroupQuery({
    skip: !shouldQuery,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  useEffect(() => {
    if (data) {
      dispatch(setGroupList(data?.chat.getListChatsGroup));
    }
  }, [data, dispatch]);

  useListenToGroupChange(
    livekitContext?.room,
    () => {
      refetch();
    },
    !(open || openMobile)
  );
  return (
    <>
      {openedGroup && <ChatList gp={openedGroup} />}
      <div
        className={`absolute w-full transform transition-all duration-300 ease-in-out 
            ${
              openedGroup
                ? "translate-x-[500px] opacity-20"
                : "translate-x-0 opacity-100"
            }`}
      >
        <div className="border-b">
          <h3 className="m-2 mx-2 font-bold text-primary">
            {t("chat_gp_list")}
          </h3>
        </div>
        {data?.chat.getListChatsGroup.map((gp, index) => (
          <div
            key={index + gp.id}
            className="p-3 border-b flex gap-1 items-center cursor-pointer hover:shadow"
            onClick={() => dispatch(setOpenedGroup(gp))}
          >
            <Avatar className="border-primary">
              <AvatarImage src={gp?.avatarUrl || ""} />
              <AvatarFallback>
                <UserRound strokeWidth={1.5} className="text-gray-600" />
              </AvatarFallback>
            </Avatar>
            <span>{gp.name}</span>
            <div className="flex items-center text-sm text-foreground/80">
              {user?.role === Role.HOST &&
                patientLine
                  ?.filter(
                    (p) =>
                      (p.role === Role.USER
                        ? gp.usersIds.includes(p.id)
                        : gp.usersIds.includes(p.visit_guest)) && p.id !== gp.id
                  )
                  .map((p) => (
                    <span key={p.id + "_" + p.fullName}>/ {p.fullName}</span>
                  ))}
            </div>
            <div className="grow flex justify-end">
              {UnreadBadge({ gpId: gp.id, unreadMessges })}
            </div>
          </div>
        ))}
        {error && <div></div>}
        {loading && (
          <>
            <div className="p-2 border-b flex gap-1 items-center">
              <div>
                <Skeleton className="h-14 w-14 rounded-full" />
              </div>
              <div className="flex flex-col gap-1">
                <Skeleton className="h-4 w-15" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            <div className="p-2 border-b flex gap-1 items-center">
              <div>
                <Skeleton className="h-14 w-14 rounded-full" />
              </div>
              <div className="flex flex-col gap-1">
                <Skeleton className="h-4 w-15" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
